import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History, RotateCcw } from 'lucide-react';

import type { WikiDiff, WikiPageVersion } from '@tasks/contracts';

import { diffVersions, listVersions, revertPage } from '../../api/wiki';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { Skeleton } from '../../components/Skeleton';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';
import styles from './WikiHistory.module.css';

export interface WikiHistoryProps {
  readonly projectKey: string;
  readonly pageId: string;
  readonly onClose: () => void;
  readonly onReverted: () => void;
}

/**
 * История версий страницы (docs/14 §10, UC-WIKI-03).
 *
 * Список версий и diff двух выбранных. Откат создаёт новую версию, а не
 * переписывает историю, поэтому кнопка доступна всегда.
 */
export function WikiHistory({ projectKey, pageId, onClose, onReverted }: WikiHistoryProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [from, setFrom] = useState<number | null>(null);
  const [to, setTo] = useState<number | null>(null);

  const versionsQuery = useQuery({
    queryKey: queryKeys.projects.wikiVersions(projectKey, pageId),
    queryFn: ({ signal }) => listVersions(projectKey, pageId, signal),
  });

  const versions = versionsQuery.data ?? [];

  const diffQuery = useQuery({
    queryKey: ['wiki', projectKey, 'diff', pageId, from, to],
    queryFn: () => diffVersions(projectKey, pageId, from as number, to as number),
    enabled: from !== null && to !== null,
  });

  const revertMutation = useMutation({
    mutationFn: (version: number) => revertPage(projectKey, pageId, version),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.projects.wikiVersions(projectKey, pageId),
      });
      toast.success('Версия восстановлена');
      onReverted();
    },
    onError: (error: unknown) => toast.error(humanizeError(error).message),
  });

  const selectVersion = (version: number): void => {
    if (from === null || to !== null) {
      setFrom(version);
      setTo(null);
      return;
    }
    setTo(version);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="История версий"
      size="lg"
      footer={<Button variant="secondary" onClick={onClose}>Закрыть</Button>}
    >
      {versionsQuery.isLoading ? (
        <Skeleton height="200px" />
      ) : (
        <div className={styles['layout']}>
          <ul className={styles['list']}>
            {versions.map((version) => (
              <VersionRow
                key={version.id}
                version={version}
                isSelected={version.version === from || version.version === to}
                onSelect={() => selectVersion(version.version)}
                onRevert={() => revertMutation.mutate(version.version)}
                isReverting={revertMutation.isPending}
              />
            ))}
          </ul>

          <section className={styles['diff']} aria-label="Разница версий">
            {from === null ? (
              <p className={styles['hint']}>
                Выберите две версии, чтобы увидеть разницу.
              </p>
            ) : to === null ? (
              <p className={styles['hint']}>
                Выбрана версия {from}. Выберите вторую для сравнения.
              </p>
            ) : diffQuery.isLoading ? (
              <Skeleton height="160px" />
            ) : diffQuery.data ? (
              <DiffView diff={diffQuery.data} />
            ) : (
              <p className={styles['hint']}>Не удалось построить разницу.</p>
            )}
          </section>
        </div>
      )}
    </Modal>
  );
}

function VersionRow({
  version,
  isSelected,
  onSelect,
  onRevert,
  isReverting,
}: {
  readonly version: WikiPageVersion;
  readonly isSelected: boolean;
  readonly onSelect: () => void;
  readonly onRevert: () => void;
  readonly isReverting: boolean;
}) {
  return (
    <li className={isSelected ? `${styles['row']} ${styles['rowActive']}` : styles['row']}>
      <button type="button" className={styles['rowButton']} onClick={onSelect}>
        <History size={14} aria-hidden />
        <span className={styles['rowTitle']}>Версия {version.version}</span>
        <span className={styles['rowMeta']}>
          {sourceLabel(version.source)} · {formatDate(version.createdAt)}
        </span>
        {version.message ? <span className={styles['rowMessage']}>{version.message}</span> : null}
      </button>
      <Button
        variant="ghost"
        size="sm"
        icon={<RotateCcw size={14} />}
        onClick={onRevert}
        loading={isReverting}
      >
        Восстановить
      </Button>
    </li>
  );
}

function DiffView({ diff }: { readonly diff: WikiDiff }) {
  return (
    <div className={styles['diffBody']}>
      <p className={styles['diffSummary']}>
        Версии {diff.from} → {diff.to}: добавлено {diff.added}, удалено {diff.removed}
      </p>
      <pre className={styles['pre']}>
        {diff.lines.map((line, index) => (
          <div
            key={`${line.kind}-${index}`}
            className={
              line.kind === 'added'
                ? styles['lineAdded']
                : line.kind === 'removed'
                  ? styles['lineRemoved']
                  : styles['lineSame']
            }
          >
            <span className={styles['lineNo']}>{line.left ?? ''}</span>
            <span className={styles['lineNo']}>{line.right ?? ''}</span>
            <span className={styles['lineText']}>
              {line.kind === 'added' ? '+ ' : line.kind === 'removed' ? '− ' : '  '}
              {line.text}
            </span>
          </div>
        ))}
      </pre>
    </div>
  );
}

function sourceLabel(source: WikiPageVersion['source']): string {
  if (source === 'import') return 'импорт';
  if (source === 'revert') return 'откат';
  if (source === 'api') return 'API';
  return 'правка';
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
